import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MuiMenuItem from '@mui/material/MenuItem';
import MuiSelect, { type SelectChangeEvent } from '@mui/material/Select';
import TablePaginationActions, {
  type TablePaginationActionsProps,
} from '@mui/material/TablePaginationActions';
import { type ReactElement, type SyntheticEvent, useState } from 'react';
import { s } from '../../theme/tokens';
import { Box } from './Box';
import { overlayScrollbarSx } from './overlayScrollbarSx';
import {
  PAGINATION_SELECT_MENU_CARET_OFFSET_PX,
  paginationSelectMenuPlacement,
  resolvePaginationSelectMenuPlacement,
} from './paginationSelectMenuPlacement';

const buildPageOptions = (pageCount: number): number[] =>
  Array.from({ length: pageCount }, (_value, index) => index);

const PAGINATION_FONT_SIZE_PX = '17px';
const PAGINATION_MENU_ITEM_HEIGHT_PX = 24;
const PAGINATION_MENU_ITEM_HEIGHT = `${PAGINATION_MENU_ITEM_HEIGHT_PX}px`;
const PAGE_SELECTOR_VISIBLE_ROWS = 20;
const PAGE_SELECTOR_MENU_MAX_HEIGHT = `${PAGINATION_MENU_ITEM_HEIGHT_PX * PAGE_SELECTOR_VISIBLE_ROWS}px`;
const PAGE_SELECTOR_MENU_MAX_HEIGHT_CSS = `${PAGE_SELECTOR_MENU_MAX_HEIGHT} !important`;
const PAGE_SELECTOR_MENU_CAP_SX = {
  height: PAGE_SELECTOR_MENU_MAX_HEIGHT_CSS,
  maxHeight: PAGE_SELECTOR_MENU_MAX_HEIGHT_CSS,
};
const PAGE_SELECTOR_MENU_CAP_STYLE = {
  height: PAGE_SELECTOR_MENU_MAX_HEIGHT,
  maxHeight: PAGE_SELECTOR_MENU_MAX_HEIGHT,
};

const buildDisplayedRowsLabel = (count: number, page: number, rowsPerPage: number): string => {
  if (count === 0) {
    return '0-0 of 0';
  }

  const from = page * rowsPerPage + 1;
  const to = count === -1 ? (page + 1) * rowsPerPage : Math.min(count, (page + 1) * rowsPerPage);
  const totalLabel = count === -1 ? `more than ${to}` : String(count);

  return `${from}-${to} of ${totalLabel}`;
};

export const TablePaginationPageActions = ({
  className,
  count,
  disabled = false,
  onPageChange,
  page,
  rowsPerPage,
  ...actionsProps
}: TablePaginationActionsProps): ReactElement => {
  const pageCount = count < 0 ? page + 1 : Math.max(1, Math.ceil(count / Math.max(1, rowsPerPage)));
  const pageOptions = buildPageOptions(pageCount);
  const pageSelectDisabled = disabled || pageCount <= 1;
  const pageMenuNeedsHeightCap = pageOptions.length > PAGE_SELECTOR_VISIBLE_ROWS;
  const displayedRowsLabel = buildDisplayedRowsLabel(count, page, rowsPerPage);
  const [pageMenuPlacement, setPageMenuPlacement] = useState(paginationSelectMenuPlacement);

  const handlePageSelectChange = (event: SelectChangeEvent<number>): void => {
    const nextPage = event.target.value;
    if (!Number.isFinite(nextPage) || nextPage === page) {
      return;
    }
    onPageChange(null, nextPage);
  };

  const handlePageSelectOpen = (event: SyntheticEvent): void => {
    setPageMenuPlacement(
      resolvePaginationSelectMenuPlacement(
        event.currentTarget as HTMLElement,
        Math.min(pageOptions.length, PAGE_SELECTOR_VISIBLE_ROWS) * PAGINATION_MENU_ITEM_HEIGHT_PX,
      ),
    );
  };

  return (
    <Box
      className={className}
      sx={{
        display: 'contents',
      }}
    >
      <Box
        className="MuiTablePagination-pageSelect"
        sx={{
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          gap: s('md'),
        }}
      >
        <Box component="p" className="MuiTablePagination-selectLabel">
          Page:
        </Box>
        <MuiSelect<number>
          className="MuiTablePagination-input"
          disabled={pageSelectDisabled}
          disableUnderline
          variant="standard"
          value={page}
          onChange={handlePageSelectChange}
          onOpen={handlePageSelectOpen}
          IconComponent={KeyboardArrowDownIcon}
          SelectDisplayProps={{
            className: 'MuiTablePagination-select',
          }}
          MenuProps={{
            ...pageMenuPlacement,
            variant: 'menu',
            PaperProps: {
              style: pageMenuNeedsHeightCap
                ? {
                    marginLeft: PAGINATION_SELECT_MENU_CARET_OFFSET_PX,
                    ...PAGE_SELECTOR_MENU_CAP_STYLE,
                  }
                : {
                    marginLeft: PAGINATION_SELECT_MENU_CARET_OFFSET_PX,
                  },
              sx: {
                minWidth: '48px',
                ...(pageMenuNeedsHeightCap ? PAGE_SELECTOR_MENU_CAP_SX : {}),
                backgroundColor: (theme) => theme.app.colors.surface,
                backgroundImage: 'none',
                border: (theme) => `1px solid ${theme.app.colors.border}`,
                borderRadius: (theme) => theme.app.radii.md,
                boxShadow: 'none',
                overflow: 'hidden',
                '--Paper-overlay': 'none',
                '--Paper-shadow': 'none',
              },
            },
            MenuListProps: {
              role: 'listbox',
              style: pageMenuNeedsHeightCap ? PAGE_SELECTOR_MENU_CAP_STYLE : undefined,
              sx: [
                {
                  ...(pageMenuNeedsHeightCap ? PAGE_SELECTOR_MENU_CAP_SX : {}),
                  overflowY: 'auto',
                  paddingTop: s('none'),
                  paddingBottom: s('none'),
                },
                overlayScrollbarSx,
              ],
            },
            slotProps: {
              paper: {
                style: pageMenuNeedsHeightCap
                  ? {
                      marginLeft: PAGINATION_SELECT_MENU_CARET_OFFSET_PX,
                      ...PAGE_SELECTOR_MENU_CAP_STYLE,
                    }
                  : {
                      marginLeft: PAGINATION_SELECT_MENU_CARET_OFFSET_PX,
                    },
                sx: {
                  minWidth: '48px',
                  ...(pageMenuNeedsHeightCap ? PAGE_SELECTOR_MENU_CAP_SX : {}),
                  backgroundColor: (theme) => theme.app.colors.surface,
                  backgroundImage: 'none',
                  border: (theme) => `1px solid ${theme.app.colors.border}`,
                  borderRadius: (theme) => theme.app.radii.md,
                  boxShadow: 'none',
                  overflow: 'hidden',
                  '--Paper-overlay': 'none',
                  '--Paper-shadow': 'none',
                },
              },
              list: {
                role: 'listbox',
                style: pageMenuNeedsHeightCap ? PAGE_SELECTOR_MENU_CAP_STYLE : undefined,
                sx: [
                  {
                    ...(pageMenuNeedsHeightCap ? PAGE_SELECTOR_MENU_CAP_SX : {}),
                    overflowY: 'auto',
                    paddingTop: s('none'),
                    paddingBottom: s('none'),
                  },
                  overlayScrollbarSx,
                ],
              },
            },
          }}
          sx={{
            minWidth: 0,
            fontFamily: (theme) => theme.typography.body.fontFamily,
            fontSize: PAGINATION_FONT_SIZE_PX,
            fontWeight: (theme) => theme.typography.body.fontWeight,
            lineHeight: (theme) => theme.typography.body.lineHeight,
            '& .MuiSelect-select': {
              paddingLeft: s('none'),
              fontFamily: (theme) => theme.typography.body.fontFamily,
              fontSize: PAGINATION_FONT_SIZE_PX,
              fontWeight: (theme) => theme.typography.body.fontWeight,
              lineHeight: (theme) => theme.typography.body.lineHeight,
            },
            '& .MuiSvgIcon-root': {
              fontSize: '20px',
            },
          }}
        >
          {pageOptions.map((pageOption) => (
            <MuiMenuItem
              key={pageOption}
              value={pageOption}
              className="MuiTablePagination-menuItem"
              sx={{
                color: (theme) => theme.app.colors.text,
                minHeight: PAGINATION_MENU_ITEM_HEIGHT,
                paddingTop: s('none'),
                paddingBottom: s('none'),
                paddingLeft: s('md'),
                paddingRight: s('md'),
                display: 'flex',
                justifyContent: 'center',
                textAlign: 'center',
                fontFamily: (theme) => theme.typography.body.fontFamily,
                fontSize: PAGINATION_FONT_SIZE_PX,
                fontWeight: (theme) => theme.typography.body.fontWeight,
                lineHeight: (theme) => theme.typography.body.lineHeight,
                backgroundColor: (theme) => `${theme.app.colors.surface} !important`,
                border: '0 !important',
                cursor: 'pointer',
                '&&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                },
                '&&.Mui-selected': {
                  backgroundColor: 'transparent !important',
                },
                '&[aria-selected="true"]': {
                  backgroundColor: 'transparent !important',
                },
                '&&.Mui-selected:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                },
                '&[aria-selected="true"]:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                },
                '&&.Mui-focusVisible': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                },
                '&&.Mui-selected.Mui-focusVisible': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                },
              }}
            >
              {pageOption + 1}
            </MuiMenuItem>
          ))}
        </MuiSelect>
      </Box>
      <Box className="MuiTablePagination-displayedRowsCustom">{displayedRowsLabel}</Box>
      <Box className="MuiTablePagination-navigation">
        <TablePaginationActions
          count={count}
          disabled={disabled}
          onPageChange={onPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
          {...actionsProps}
        />
      </Box>
    </Box>
  );
};
